import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { PhotoIcon } from "@heroicons/react/24/outline";

export default function EventSection() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/v1/events/all`);
        setEvents(response.data);
      } catch (err) {
        console.error("Error fetching events:", err);
        setError("Failed to fetch events. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  if (loading) {
    return <p className="text-lg text-center text-gray-500">Loading upcoming events...</p>;
  }

  if (error) {
    return <p className="text-center text-red-600">No upcoming events.</p>;
  }

  return (
    <div className="container mx-auto p-14 bg-gradient-to-r from-teal-50 to-blue-50">
      <h1 className="mb-8 text-4xl font-bold text-center text-teal-600">Upcoming College Events</h1>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {events.map((event, index) => (
          <motion.div
            key={event._id} // Use a unique key like event._id instead of index
            className="flex flex-col overflow-hidden transition-shadow duration-300 ease-in-out bg-white border-t-4 border-teal-600 rounded-lg shadow-lg hover:shadow-xl"
            whileHover={{ scale: 1.03 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              opacity: { duration: 0.5, ease: "easeInOut" },
              scale: { type: "spring", stiffness: 300, damping: 20 },
              delay: index * 0.1,
            }}
          >
            <div className="flex flex-col flex-grow p-6">
              <h2 className="mb-2 text-2xl font-semibold text-teal-700">{event.title}</h2>

              {/* Render images */}
              {event.images && event.images.length > 0 ? (
                event.images.map((image, idx) => (
                  <div className="mb-4" key={idx}>
                    <img
                      src={`/api/uploads/events/${image}`}
                      alt={`${event.title}`}
                      className="object-cover w-full h-48 rounded"
                    />
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center w-20 h-20 text-gray-400 bg-gray-200 rounded">
                  <PhotoIcon className="w-6 h-6" />
                </div>
              )}

              {/* Render videos */}
              {event.videos && event.videos.length > 0 && (
                <div className="mb-4 space-y-4">
                  {event.videos.map((url, idx) => (
                    <video
                      key={idx}
                      src={`/api/uploads/events/${url}`}
                      controls
                      className="object-cover w-full h-48 rounded"
                    >
                      Your browser does not support the video tag.
                    </video>
                  ))}
                </div>
              )}

              <p className="mb-4 text-base text-gray-600">{event.description}</p>
              <p className="mb-2 text-sm text-gray-500">
                <strong>Date:</strong> {new Date(event.date).toLocaleDateString()}
              </p>
              <p className="mb-2 text-sm text-gray-500">
                <strong>Location:</strong> {event.location}
              </p>
              <p className="mb-4 text-sm text-gray-500">
                <strong>Organizer:</strong> {event.organizer}
              </p>
              <a
                href={event.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-4 py-2 mt-auto text-sm font-medium text-white transition-colors duration-300 bg-teal-600 rounded hover:bg-teal-700"
              >
                More Details
              </a>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
